/**
 * Frontend logging utility for sending error logs to the backend
 * Only errors and warnings are sent to the cloud logging system
 * Navigation and API operations are logged for tracking user flows
 */

const LOG_ENDPOINT =  process.env.REACT_APP_API_BASE_URL+'/logs';

// Feature flag to control cloud logging
const CLOUD_LOGGING_ENABLED = process.env.REACT_APP_ENABLE_CLOUD_LOGGING === 'true';

/**
 * Send log to backend
 * @param {string} level - Log level (error, warning, info, debug, navigation, api)
 * @param {string} message - Log message
 * @param {Object} metadata - Additional metadata
 */
const sendLogToBackend = async (level, message, metadata = {}) => {
    // Check if cloud logging is enabled via feature flag
    if (!CLOUD_LOGGING_ENABLED) {
        console.debug('[CloudLogger] Cloud logging disabled via feature flag REACT_APP_ENABLE_CLOUD_LOGGING');
        return;
    }

    // Send errors, warnings, navigation, and API logs to cloud (only for authenticated users)
    const cloudLevels = ['error', 'warning', 'navigation', 'api'];
    if (!cloudLevels.includes(level.toLowerCase())) {
        return;
    }
    
    // Check if user is authenticated before attempting to send logs
    const token = localStorage.getItem('authToken');
    if (!token) {
        console.debug('[CloudLogger] No auth token found, skipping cloud logging');
        return;
    }
    
    try {
        const logData = {
            level,
            message,
            metadata: {
                ...metadata,
                userAgent: navigator.userAgent,
                url: window.location.href,
                timestamp: new Date().toISOString(),
                service: 'frontend',
                sessionId: getSessionId(),
                userId: getCurrentUserId(),
                userEmail: getCurrentUserEmail()
            }
        };

        const response = await fetch(LOG_ENDPOINT, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}` // Include auth token
            },
            body: JSON.stringify(logData),
        });

        // Handle authentication errors
        if (response.status === 401) {
            console.debug('[CloudLogger] Authentication failed for logging, token may be expired');
            // Don't retry or throw error, just fail silently for logging
            return;
        }

        if (!response.ok) {
            throw new Error(`HTTP ${response.status}: ${response.statusText}`);
        }

    } catch (error) {
        // Fallback to console if backend logging fails
        console.error('Failed to send log to backend:', error);
        console.error(`[${level.toUpperCase()}] ${message}`, metadata);
        
        // If it's a network error or 500, we might want to queue logs for retry
        // For now, we just log locally as fallback
    }
};

/**
 * Check if user is authenticated
 */
const isUserAuthenticated = () => {
    const token = localStorage.getItem('authToken');
    if (!token) return false;
    
    try {
        const payload = JSON.parse(atob(token.split('.')[1]));
        const currentTime = Date.now() / 1000;
        return payload.exp > currentTime;
    } catch {
        return false;
    }
};

/**
 * Get session ID from localStorage or create new one
 */
const getSessionId = () => {
    let sessionId = localStorage.getItem('sessionId');
    if (!sessionId) {
        sessionId = `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
        localStorage.setItem('sessionId', sessionId);
    }
    return sessionId;
};

/**
 * Get current user ID from token
 */
const getCurrentUserId = () => {
    try {
        const token = localStorage.getItem('authToken');
        if (!token) return null;
        const payload = JSON.parse(atob(token.split('.')[1]));
        return payload.id || payload.userId;
    } catch {
        return null;
    }
};

/**
 * Get current user email from token
 */
const getCurrentUserEmail = () => {
    try {
        const token = localStorage.getItem('authToken');
        if (!token) return null;
        const payload = JSON.parse(atob(token.split('.')[1]));
        return payload.email;
    } catch {
        return null;
    }
};

/**
 * Check if cloud logging is enabled
 */
export const isCloudLoggingEnabled = () => {
    return CLOUD_LOGGING_ENABLED;
};

/**
 * Get cloud logging configuration status
 */
export const getCloudLoggingStatus = () => {
    return {
        enabled: CLOUD_LOGGING_ENABLED,
        endpoint: LOG_ENDPOINT,
        userAuthenticated: isUserAuthenticated(),
        featureFlag: process.env.REACT_APP_ENABLE_CLOUD_LOGGING
    };
};

/**
 * Log error to cloud
 */
export const logError = (message, metadata = {}) => {
    console.error(`[ERROR] ${message}`, metadata);
    if (CLOUD_LOGGING_ENABLED) {
        sendLogToBackend('error', message, metadata);
    } else {
        console.debug('[CloudLogger] Error not sent to cloud - feature flag disabled');
    }
};

/**
 * Log warning to cloud
 */
export const logWarning = (message, metadata = {}) => {
    console.warn(`[WARNING] ${message}`, metadata);
    if (CLOUD_LOGGING_ENABLED) {
        sendLogToBackend('warning', message, metadata);
    } else {
        console.debug('[CloudLogger] Warning not sent to cloud - feature flag disabled');
    }
};

/**
 * Log info (console only, not sent to cloud)
 */
export const logInfo = (message, metadata = {}) => {
    console.info(`[INFO] ${message}`, metadata);
};

/**
 * Log debug (console only, not sent to cloud)
 */
export const logDebug = (message, metadata = {}) => {
    console.debug(`[DEBUG] ${message}`, metadata);
};

/**
 * Global error handler for unhandled errors
 * Only logs to cloud if user is authenticated and cloud logging is enabled
 */
export const setupGlobalErrorHandling = () => {
    console.log(`[CloudLogger] Setting up global error handling. Cloud logging enabled: ${CLOUD_LOGGING_ENABLED}`);
    
    // Handle unhandled JavaScript errors
    window.addEventListener('error', (event) => {
        // Always log to console for debugging
        console.error('[ERROR] Unhandled JavaScript error', {
            message: event.message,
            filename: event.filename,
            lineno: event.lineno,
            colno: event.colno,
            stack: event.error?.stack
        });

        // Only log to cloud if feature flag is enabled and user is authenticated
        if (CLOUD_LOGGING_ENABLED && isUserAuthenticated()) {
            logError('Unhandled JavaScript error', {
                message: event.message,
                filename: event.filename,
                lineno: event.lineno,
                colno: event.colno,
                stack: event.error?.stack
            });
        } else {
            const reason = !CLOUD_LOGGING_ENABLED ? 'cloud logging disabled' : 'user not authenticated';
            console.debug(`[CloudLogger] Error not sent to cloud - ${reason}`);
        }
    });

    // Handle unhandled promise rejections
    window.addEventListener('unhandledrejection', (event) => {
        // Always log to console for debugging
        console.error('[ERROR] Unhandled promise rejection', {
            reason: event.reason?.toString(),
            stack: event.reason?.stack
        });

        // Only log to cloud if feature flag is enabled and user is authenticated
        if (CLOUD_LOGGING_ENABLED && isUserAuthenticated()) {
            logError('Unhandled promise rejection', {
                reason: event.reason?.toString(),
                stack: event.reason?.stack
            });
        } else {
            const reason = !CLOUD_LOGGING_ENABLED ? 'cloud logging disabled' : 'user not authenticated';
            console.debug(`[CloudLogger] Promise rejection not sent to cloud - ${reason}`);
        }
    });
};

/**
 * Log API call errors with context
 */
export const logApiError = (endpoint, error, requestData = {}) => {
    logError(`API call failed: ${endpoint}`, {
        endpoint,
        error: error.message,
        stack: error.stack,
        requestData,
        httpStatus: error.status || error.statusCode
    });
};

/**
 * Log payment errors with context
 */
export const logPaymentError = (provider, error, orderData = {}) => {
    logError(`Payment error: ${provider}`, {
        paymentProvider: provider,
        error: error.message,
        orderData: {
            // Only log non-sensitive order data
            orderId: orderData.orderId,
            amount: orderData.amount,
            currency: orderData.currency
        }
    });
};

/**
 * Log shipping errors with context
 */
export const logShippingError = (operation, error, shippingData = {}) => {
    logError(`Shipping error: ${operation}`, {
        operation,
        error: error.message,
        shippingData: {
            // Only log non-sensitive shipping data
            rateId: shippingData.rateId,
            provider: shippingData.provider,
            service: shippingData.service
        }
    });
};

/**
 * Log navigation events (sent to cloud for user flow analysis)
 */
export const logNavigation = (fromPath, toPath, navigationData = {}) => {
    const message = `Navigation: ${fromPath || 'unknown'} -> ${toPath}`;
    console.info(`[NAVIGATION] ${message}`, navigationData);
    
    if (CLOUD_LOGGING_ENABLED) {
        sendLogToBackend('navigation', message, {
            ...navigationData,
            fromPath,
            toPath,
            navigationType: navigationData.trigger || 'unknown', // e.g., 'click', 'programmatic', 'browser'
            timestamp: new Date().toISOString()
        });
    } else {
        console.debug('[CloudLogger] Navigation not sent to cloud - feature flag disabled');
    }
};

/**
 * Log API call entry (start of API operation)
 */
export const logApiEntry = (endpoint, method = 'GET', requestData = {}) => {
    const message = `API Entry: ${method} ${endpoint}`;
    console.debug(`[API_ENTRY] ${message}`, requestData);
    
    if (CLOUD_LOGGING_ENABLED) {
        sendLogToBackend('api', message, {
            endpoint,
            method,
            requestData: sanitizeRequestData(requestData),
            operation: 'entry',
            timestamp: new Date().toISOString()
        });
    }
};

/**
 * Log API call exit (end of API operation)
 */
export const logApiExit = (endpoint, method = 'GET', responseData = {}, duration = null, success = true) => {
    const status = success ? 'success' : 'failure';
    const message = `API Exit: ${method} ${endpoint} - ${status}`;
    console.debug(`[API_EXIT] ${message}`, { responseData, duration });
    
    if (CLOUD_LOGGING_ENABLED) {
        sendLogToBackend('api', message, {
            endpoint,
            method,
            responseData: sanitizeResponseData(responseData),
            operation: 'exit',
            success,
            duration,
            timestamp: new Date().toISOString()
        });
    }
};

/**
 * Log complete API operation (entry + exit combined)
 */
export const logApiOperation = async (endpoint, method = 'GET', apiCall, requestData = {}) => {
    const startTime = Date.now();
    logApiEntry(endpoint, method, requestData);
    
    try {
        const response = await apiCall();
        const duration = Date.now() - startTime;
        logApiExit(endpoint, method, response, duration, true);
        return response;
    } catch (error) {
        const duration = Date.now() - startTime;
        logApiExit(endpoint, method, { error: error.message }, duration, false);
        logApiError(endpoint, error, requestData);
        throw error;
    }
};

/**
 * Sanitize request data to remove sensitive information
 */
const sanitizeRequestData = (data) => {
    if (!data || typeof data !== 'object') return data;
    
    const sanitized = { ...data };
    const sensitiveKeys = ['password', 'token', 'secret', 'key', 'auth', 'creditCard', 'cvv', 'ssn'];
    
    sensitiveKeys.forEach(key => {
        if (sanitized[key]) {
            sanitized[key] = '[REDACTED]';
        }
    });
    
    return sanitized;
};

/**
 * Sanitize response data to remove sensitive information
 */
const sanitizeResponseData = (data) => {
    if (!data || typeof data !== 'object') return data;
    
    const sanitized = { ...data };
    const sensitiveKeys = ['password', 'token', 'secret', 'key', 'auth', 'creditCard'];
    
    sensitiveKeys.forEach(key => {
        if (sanitized[key]) {
            sanitized[key] = '[REDACTED]';
        }
    });
    
    // Only log success status and basic info for responses
    return {
        success: sanitized.success,
        status: sanitized.status,
        message: sanitized.message,
        dataLength: Array.isArray(sanitized.data) ? sanitized.data.length : 
                   sanitized.data ? Object.keys(sanitized.data).length : 0
    };
};

/**
 * Export authentication check for external use
 */
export const isAuthenticated = isUserAuthenticated;

/**
 * Log authentication status change (login/logout)
 */
export const logAuthenticationChange = (action, userInfo = {}) => {
    const message = `User ${action}`;
    console.info(`[AUTH] ${message}`, userInfo);
    
    if (!CLOUD_LOGGING_ENABLED) {
        console.debug('[CloudLogger] Authentication change not sent to cloud - feature flag disabled');
        return;
    }

    if (action === 'login' && userInfo.email) {
        // Log successful authentication
        sendLogToBackend('navigation', message, {
            action,
            userEmail: userInfo.email,
            userId: userInfo.id,
            loginMethod: userInfo.method || 'unknown'
        });
    } else if (action === 'logout') {
        // Log logout (will be sent before token is removed)
        sendLogToBackend('navigation', message, {
            action,
            reason: userInfo.reason || 'user_initiated'
        });
    }
};

/**
 * Log section change within the application
 */
export const logSectionChange = (section, sectionData = {}) => {
    const fromSection = sectionData.previousSection || 'unknown';
    const toSection = section;
    const message = `Section change: ${fromSection} -> ${toSection}`;
    console.info(`[SECTION] ${message}`, sectionData);
    
    if (CLOUD_LOGGING_ENABLED) {
        sendLogToBackend('navigation', message, {
            action: 'section_change',
            fromSection,
            toSection,
            navigationMethod: sectionData.method || 'unknown',
            ...sectionData
        });
    } else {
        console.debug('[CloudLogger] Section change not sent to cloud - feature flag disabled');
    }
};
